import React, { createContext, useContext, useReducer, useCallback, useState } from "react";
import { FormErrorState, InvoiceFormErrorState } from "types/validationError.types";

export interface PaymentFormData {
  firstName: string;
  lastName: string;
  dob: string;
  formattedDate: string;
  phone: string;
  email: string;
  agreed: boolean;
  selectedBusiness: string;
  companyName: string;
  address: string;
  zipcode: string;
  city: string;
  selectedCountry: string;
  retainedBusinessValue: string;
  paymentMethod: string;
  isOverviewPage?: boolean;
  bankaccholder?: string;
  iban?: string,
  isPaymentMethodChanged?: boolean;
}

export type PaymentFormErrors = Omit<FormErrorState, "notAgreedError"> | Omit<InvoiceFormErrorState, "notAgreedError">;

interface PaymentFormContextProps {
  PaymentFormData: PaymentFormData;
  setPaymentFormData: (data: PaymentFormData) => void;
  resetForm: () => void;
  setOverviewPage: (value: boolean) => void;
  PaymentFormErrors: PaymentFormErrors;
  setPaymentFormErrors: (data: PaymentFormErrors) => void;
}

const initialState: PaymentFormData = {
  firstName: "",
  lastName: "",
  dob: "",
  formattedDate: "",
  phone: "",
  email: "",
  agreed: false,
  selectedBusiness: "Business to Customer",
  companyName: "",
  address: "",
  zipcode: "",
  city: "",
  selectedCountry: "Germany",
  retainedBusinessValue: "",
  paymentMethod: "",
  isOverviewPage: false,
  bankaccholder: "",
  isPaymentMethodChanged: false,
  iban: "",
};

const initialErrorState = {
  customerError: {
    companyNameError: "",
    firstNameError: "",
    lastNameError: "",
    dobError: "",
  },
  contactError: {
    emailError: "",
    phoneError: "",
  },
  billingError: {
    addressError: "",
    zipcodeError: "",
    cityError: "",
    countryError: ""
  },
  paymentError: {
    iban: "",
    bankAccountHolderError: "",
  },
};

export const clearEmptyErrors = (errors: PaymentFormErrors) =>
  Object.fromEntries(
    Object.entries(errors).map(([section, fields]) => [
      section,
      Object.fromEntries(
        Object.entries(fields).map(([field, value]) => [
          field,
          value.includes("empty") ? "" : value
        ])
      )
    ])
  ) as PaymentFormErrors;

type PaymentFormAction =
  | { type: "SET_FORM_DATA"; payload: PaymentFormData }
  | { type: "SET_OVERVIEW_PAGE"; payload: boolean }
  | { type: "RESET_FORM" };

const formReducer = (state: PaymentFormData, action: PaymentFormAction): PaymentFormData => {
  switch (action.type) {
    case "SET_FORM_DATA":
      return action.payload;

    case "RESET_FORM":
      return initialState;

    case "SET_OVERVIEW_PAGE":
      return { ...state, isOverviewPage: action.payload };
    default:
      return state;
  }
};

const PaymentFormContext = createContext<PaymentFormContextProps>({
  PaymentFormData: initialState,
  setPaymentFormData: () => { },
  resetForm: () => { },
  setOverviewPage: () => { },
  PaymentFormErrors: initialErrorState,
  setPaymentFormErrors: () => {},
});

export const PaymentFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [PaymentFormData, dispatch] = useReducer(formReducer, initialState);

  const setPaymentFormData = useCallback((data: PaymentFormData) => {
    dispatch({
      type: "SET_FORM_DATA",
      payload: { ...data, isOverviewPage: data.isOverviewPage },
    });
  }, []);

  const [PaymentFormErrors, setFormErrors] = useState<PaymentFormErrors>(initialErrorState);

  const setPaymentFormErrors = (errors: PaymentFormErrors) => {
    setFormErrors(errors);
  }

  const resetForm = useCallback(() => {
    dispatch({ type: "RESET_FORM" });
  }, []);

  const setOverviewPage = useCallback((value: boolean) => {
    dispatch({ type: "SET_OVERVIEW_PAGE", payload: value });
  }, []);

  const value: PaymentFormContextProps = {
    PaymentFormData,
    setPaymentFormData,
    resetForm,
    setOverviewPage,
    PaymentFormErrors,
    setPaymentFormErrors
  };

  return (
    <PaymentFormContext.Provider value={value}>
      {children}
    </PaymentFormContext.Provider>
  );
};

export const usePaymentForm = () => useContext(PaymentFormContext);